import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import FeatherIcon from 'feather-icons-react';
import IntroHero from '../../../components/intro/IntroHero';
import {
    INTRO_CATEGORIES,
    INTRO_HERO_FEATURES,
    INTRO_FAQ,
    SITE_URL,
    SITE_NAME,
    OG_IMAGE,
    APPSTORE_URL,
    TWITTER_HANDLE,
    SOFTWARE_APP_LD,
    ORGANIZATION_LD,
    FAQ_PAGE_LD
} from '../../../data/introFeatures';
import './intro.scss';

const IntroIndex = () => {
    const META = {
        title: '온리플 — 카페24 리뷰 솔루션 | AI 분석·위젯·자동 동기화',
        description: '카페24 리뷰 솔루션 온리플. AI 분석·자동 동기화·다양한 위젯·작성 유도까지 한 번에. 쌓아둔 리뷰가 매출이 되는 가장 빠른 길, 14일 무료로 만나보세요.',
        keywords: '리뷰 솔루션, 온리플, 카페24 리뷰, 쇼핑몰 리뷰 솔루션, 리뷰 관리, AI 리뷰 분석, 리뷰 위젯, 리뷰 자동 동기화, 알림톡 리뷰',
        canonical: SITE_URL + '/intro'
    };

    return (
        <div className="intro">
            <Helmet>
                <html lang="ko" />
                <title>{META.title}</title>
                <meta name="description" content={META.description} />
                <meta name="keywords" content={META.keywords} />
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
                <meta name="author" content="ONNOMADS" />
                <link rel="canonical" href={META.canonical} />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content={SITE_NAME} />
                <meta property="og:locale" content="ko_KR" />
                <meta property="og:title" content={META.title} />
                <meta property="og:description" content={META.description} />
                <meta property="og:image" content={OG_IMAGE} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content="온리플 — 카페24 리뷰 솔루션" />
                <meta property="og:url" content={META.canonical} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content={TWITTER_HANDLE} />
                <meta name="twitter:title" content={META.title} />
                <meta name="twitter:description" content={META.description} />
                <meta name="twitter:image" content={OG_IMAGE} />

                {/* Structured Data */}
                <script type="application/ld+json">{JSON.stringify(SOFTWARE_APP_LD)}</script>
                <script type="application/ld+json">{JSON.stringify(ORGANIZATION_LD)}</script>
                <script type="application/ld+json">{JSON.stringify(FAQ_PAGE_LD)}</script>
            </Helmet>

            <IntroHero />

            {/* 핵심 가치 6 */}
            <section className="intro-section">
                <div className="intro-section__inner">
                    <div className="intro-section__head">
                        <span className="intro-section__chip">Why 온리플</span>
                        <h2>리뷰 운영, 더 이상 어렵지 않게</h2>
                        <p>설치부터 운영, 그리고 성과 측정까지 — 한 솔루션이면 충분합니다.</p>
                    </div>
                    <div className="intro-value-grid">
                        {INTRO_HERO_FEATURES.map(v => (
                            <div key={v.title} className="intro-value">
                                <span className="intro-value__icon">
                                    <FeatherIcon icon={v.icon} size="22" />
                                </span>
                                <h3>{v.title}</h3>
                                <p>{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 카테고리 진입 카드 */}
            <section className="intro-section intro-section--alt">
                <div className="intro-section__inner">
                    <div className="intro-section__head">
                        <span className="intro-section__chip intro-section__chip--pink">기능 둘러보기</span>
                        <h2>카테고리로 만나는 온리플</h2>
                        <p>궁금하신 영역부터 차근차근 살펴보세요.</p>
                    </div>
                    <div className="intro-cat-grid">
                        {INTRO_CATEGORIES.map(c => (
                            <Link key={c.slug} to={`/intro/${c.slug}`} className="intro-cat-card">
                                <span className="intro-cat-card__icon">
                                    <FeatherIcon icon={c.icon} size="22" />
                                </span>
                                <h3>{c.title}</h3>
                                <p>{c.summary}</p>
                                <span className="intro-cat-card__count">{c.features.length}개 기능</span>
                                <span className="intro-cat-card__arrow">
                                    <FeatherIcon icon="arrow-right" size="16" />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* 사회적 증거 */}
            <section className="intro-section intro-proof">
                <div className="intro-section__inner">
                    <div className="intro-proof__grid">
                        <div className="intro-proof__item">
                            <strong>1,200+</strong>
                            <span>함께하는 스토어</span>
                        </div>
                        <div className="intro-proof__item">
                            <strong>4.5 / 5.0</strong>
                            <span>앱스토어 평점</span>
                        </div>
                        <div className="intro-proof__item">
                            <strong>14일</strong>
                            <span>무료로 만나보기</span>
                        </div>
                        <div className="intro-proof__item">
                            <strong>5분</strong>
                            <span>설치까지 걸리는 시간</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="intro-section">
                <div className="intro-section__inner intro-section__inner--narrow">
                    <div className="intro-section__head">
                        <span className="intro-section__chip">FAQ</span>
                        <h2>자주 묻는 질문</h2>
                    </div>
                    <div className="intro-faq">
                        {INTRO_FAQ.map((f, i) => (
                            <details key={i} className="intro-faq__item">
                                <summary>
                                    <span className="intro-faq__q-mark">Q</span>
                                    <span>{f.q}</span>
                                    <FeatherIcon icon="chevron-down" size="16" className="intro-faq__chev" />
                                </summary>
                                <div className="intro-faq__a">
                                    <div className="intro-faq__a-bubble">
                                        <span className="intro-faq__a-mark" aria-hidden="true">A</span>
                                        <p className="intro-faq__a-text">{f.a}</p>
                                    </div>
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* 최종 CTA */}
            <section className="intro-cta-band">
                <div className="intro-section__inner">
                    <h2>오늘부터, 14일 동안 무료로 만나보세요</h2>
                    <p>결제 정보 없이도 모든 기능을 활짝 열어드립니다.</p>
                    <div className="intro-cta-band__btns">
                        <a href={APPSTORE_URL} target="_blank" rel="noopener noreferrer" className="intro-btn intro-btn--primary">
                            카페24 앱스토어에서 시작하기 <FeatherIcon icon="external-link" size="14" />
                        </a>
                        <a href={APPSTORE_URL} target="_blank" rel="noopener noreferrer" className="intro-btn intro-btn--white">
                            무료 사용하기 <FeatherIcon icon="external-link" size="14" />
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default IntroIndex;
